import FaroSphereLogo from "@assets/logo/faro-sphere-xg-logo-color.svg?react";
import FaroSphereLogoMini from "@assets/logo/faro-sphere-xg-logo-mini.svg?react";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { PreservingNavLink } from "@components/common/preserve-navigation/preserving-nav-link";
import { AnalyticsFeedbackDialog } from "@components/analytics/in-house/feedback/analytics-feedback-dialog";
import { shouldShowAnalyticsFeedback } from "@components/analytics/in-house/feedback/analytics-feedback-storage";
import { SdbCompanyMenu } from "@components/sidebar/sdb-company/sdb-company-menu";
import { SidebarHolobuilderItem } from "@components/sidebar/sidebar-holobuilder/sidebar-holobuilder-item";
import { SidebarItems } from "@components/sidebar/sidebar-items";
import { SidebarList } from "@components/sidebar/sidebar-list";
import { SidebarRouteExitDialog } from "@components/sidebar/sidebar-route-exit-dialog";
import { TrialCountdown } from "@components/sidebar/trial-countdown/trial-countdown";
import { Box, ListItem } from "@mui/material";
import { MainRoutes } from "@router/route-params";
import { useAppDispatch } from "@store/store-helper";
import { updateSidebar } from "@store/ui/ui-slice";
import { neutral, CollapseArrowIcon, ExpandArrowIcon } from "@flat-ui";
import { CommonStyles } from "@styles/common-styles";
import { SidebarEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";

interface Props {
  /** Flag whether the sidebar should be shown full size or minified */
  isSidebarFullSize: boolean;

  /** Flag whether the sidebar should be shown in mobile (temporary) version */
  isSidebarMobileVersion: boolean;
}

export function SidebarContent({
  isSidebarFullSize,
  isSidebarMobileVersion,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const { canViewPremiumAnalytics } = useHasUserValidFeatureCompanyLevel();

  const logoSize = useMemo(
    () => (isSidebarMobileVersion ? "3rem" : "3.25rem"),
    [isSidebarMobileVersion]
  );

  return (
    <>
      {/* Top components */}
      <div>
        <PreservingNavLink to={MainRoutes.projects}>
          <Box
            component="div"
            sx={{
              pb: "0.25rem",
              pt: "0.375rem",
              pl: isSidebarFullSize ? "0" : "0.438rem",
            }}
            onClick={() => {
              trackEvent({ name: SidebarEvents.goHome });
            }}
          >
            {isSidebarFullSize ? (
              <FaroSphereLogo height={logoSize} />
            ) : (
              <FaroSphereLogoMini height={logoSize} />
            )}
          </Box>
        </PreservingNavLink>
        <SdbCompanyMenu />
        <SidebarList
          isSidebarFullSize={isSidebarFullSize}
          sidebarItems={SidebarItems}
        />
      </div>

      {/* Shows a feedback dialog when the user navigates away from analytics premium */}
      {canViewPremiumAnalytics && (
        <SidebarRouteExitDialog
          route={MainRoutes.analytics}
          canOpen={shouldShowAnalyticsFeedback}
          renderDialog={(close) => <AnalyticsFeedbackDialog onClose={close} />}
        />
      )}

      {/* Bottom components */}
      <div>
        {/* Counter of trial subscription days left */}
        <TrialCountdown />
        {/* Switch to HoloBuilder button */}
        {/* This component handles its visibility in the sidebar internally. */}
        <SidebarHolobuilderItem isSidebarFullSize={isSidebarFullSize} />
        {/* Button to toggle the full size state of the side bar (not available on mobile version) */}
        {!isSidebarMobileVersion && (
          <ListItem
            sx={{
              width: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: isSidebarFullSize ? "flex-end" : "center",
              borderTop: CommonStyles.Borders.gray200Divider,
              color: neutral[800],
              minHeight: "64px",
            }}
          >
            <SphereIconButton
              dataTestId="SIDEBAR_TOGGLE_BUTTON"
              component={
                isSidebarFullSize ? CollapseArrowIcon : ExpandArrowIcon
              }
              onClick={() => {
                trackEvent({
                  name: isSidebarFullSize
                    ? SidebarEvents.collapse
                    : SidebarEvents.expand,
                });
                dispatch(updateSidebar({ isFullSize: !isSidebarFullSize }));
              }}
            />
          </ListItem>
        )}
      </div>
    </>
  );
}
